import React from "react";
import { Spinner } from "components/Loader";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import { useConnection, useLinks } from "hooks";
import { colors, fontSize } from "style";
import { Col, Row } from "components/Containers";
import { BUTTON_SIZE, DestructableButton, SecondaryButton } from "components/Buttons";
import Card from "components/Card";
import { SocialIcon, SOCIAL_ICON_TYPE } from "components/Icon";
import Status from "components/Status";
import { H5, SubTitle } from "components/Text";
import { GET_CONNECTIONS } from "graphql/queries";

/**
 * CalendlyIntegration
 *
 * @param {Object}    connection
 * @param {boolean}   loading
 */
const CalendlyIntegration = ({ connection, loading }) => {
  const links = useLinks();
  const { removeCalendlyConnection, removingCalendlyConnection } = useConnection({ shouldSkip: true });

  if (loading) return <Spinner size="2rem" margin="1rem auto" />;

  return (
    <Card xsmallPadding noMargin>
      <Row gap="1.5rem">
        <Row noWrap gap="1.2rem" css={styles.integration}>
          <SocialIcon type={SOCIAL_ICON_TYPE.calendly} css={styles.icon} aria-hidden="true" />

          <Col gap="0.5rem">
            <H5 css={styles.title}>
              <FormattedMessage id="UserSettings.InterviewScheduling.Calendly.Title" />
            </H5>

            <SubTitle css={styles.description}>
              <FormattedMessage id="UserSettings.InterviewScheduling.Calendly.Description" />
            </SubTitle>
          </Col>
        </Row>

        {connection?.enabled ? (
          <Row noWrap gap="1rem">
            <Status enabled>
              <FormattedMessage id="UserSettings.InterviewScheduling.Calendly.Connected" />
            </Status>

            <DestructableButton
              size={BUTTON_SIZE.small}
              onClick={() => removeCalendlyConnection(connection.id, [GET_CONNECTIONS])}
              disabled={removingCalendlyConnection}
              css={styles.disconnect}
            >
              <FormattedMessage id="Global.Disconnect" />
            </DestructableButton>
          </Row>
        ) : (
          <SecondaryButton as="a" href={links.calendly_connect} size={BUTTON_SIZE.small} css={styles.connect}>
            <FormattedMessage id="UserSettings.InterviewScheduling.Calendly.Connect" />
          </SecondaryButton>
        )}
      </Row>
    </Card>
  );
};

const styles = {
  integration: css`
    flex: 1 1 22rem;
    min-width: 0;
  `,
  icon: css`
    flex-shrink: 0;
  `,
  title: css`
    margin: 0;
    font-size: ${fontSize.small};
  `,
  description: css`
    margin-left: 0;
    line-height: 1.5;
  `,
  connect: css`
    border: 1px solid ${colors.grayAnatomyLight4};
    box-shadow: none;
  `,
  disconnect: css`
    border: 1px solid ${colors.grayAnatomyLight4};
    box-shadow: none;
  `,
};

CalendlyIntegration.propTypes = {
  connection: PropTypes.object,
  loading: PropTypes.bool,
};

export default CalendlyIntegration;
